import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { AnimatePresence } from 'framer-motion';
import { ThemeProvider } from 'next-themes';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';
import Layout from './components/Layout';
import PageTransition from './components/PageTransition';
import SEOHead from './components/SEOHead';
import { AuthProvider } from './contexts/AuthContext';
import { SystemMessageBanner } from './components/SystemMessageBanner';
import './App.css';
const HomePage = lazy(() => import('./pages/HomePage'));
const PlayerPage = lazy(() => import('./pages/PlayerPage'));
const CalculatorPage = lazy(() => import('./pages/CalculatorPage'));
const ClubPage = lazy(() => import('./pages/ClubPage'));
const LeaguePage = lazy(() => import('./pages/LeaguePage'));
const TournamentPage = lazy(() => import('./pages/TournamentPage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const CodesPage = lazy(() => import('./pages/CodesPage'));
const CodeGeneratorPage = lazy(() => import('./pages/CodeGeneratorPage'));
const WidgetPage = lazy(() => import('./pages/WidgetPage'));
const WidgetConfiguratorPage = lazy(() => import('./pages/WidgetConfiguratorPage'));
// DISABLED: TTStats Wrapped
// import Wrapped2025Page from './pages/Wrapped2025Page';
const ProPage = lazy(() => import('./pages/ProPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const RankingsLandingPage = lazy(() => import('./pages/RankingsLandingPage'));

// Component to handle redirects with parameters
const RedirectWithParams: React.FC<{ to: string }> = ({ to }) => {
  const params = useParams();
  let redirectPath = to;

  // Replace all parameter placeholders with actual values
  Object.entries(params).forEach(([key, value]) => {
    redirectPath = redirectPath.replace(`:${key}`, value as string);
  });

  return <Navigate to={redirectPath} replace />;
};


const theme = createTheme({
  primaryColor: 'blue',
  colors: {
    blue: [
      '#e6f2ff',
      '#baddff',
      '#8dc8ff',
      '#61b3ff',
      '#349eff',
      '#2563eb', // Main blue at index 5
      '#1C7AD6', // TTStats blue at index 6
      '#1862b3',
      '#144e8f',
      '#103a6b'
    ],
  },
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  defaultRadius: 'md',
});

const routeFallback = (
  <div className="min-h-[40vh] w-full" aria-hidden="true" />
);

function AppContent() {
  const location = useLocation();
  const prevPathRef = React.useRef<string | null>(null);

  // Track last in-app path so "Back" is deterministic even for deep links/new tabs.
  React.useEffect(() => {
    const current = location.pathname + location.search + location.hash;
    const prev = prevPathRef.current;

    if (prev && prev !== current) {
      try {
        sessionStorage.setItem('ttstats:last_path', prev);
      } catch {
        // ignore storage issues (private mode, etc.)
      }
    }

    prevPathRef.current = current;
  }, [location.pathname, location.search, location.hash]);

  // Extract language from pathname (e.g., /fr/player/123 -> fr)
  const pathParts = location.pathname.split('/').filter(Boolean);
  const langFromPath = pathParts[0];
  const validLangs = ['en', 'fr', 'de', 'it'];
  const currentLang = (validLangs.includes(langFromPath) ? langFromPath : 'en') as 'en' | 'fr' | 'de' | 'it';

  // Check if this is a widget route (no layout needed)
  const isWidgetRoute = location.pathname.startsWith('/widget/');
  // DISABLED: TTStats Wrapped
  // const isWrappedRoute = location.pathname.includes('/wrapped/2025/');

  if (isWidgetRoute) {
    return (
      <Suspense fallback={routeFallback}>
        <Routes location={location} key={location.pathname}>
          <Route path="/widget/:clubId" element={<WidgetPage />} />
        </Routes>
      </Suspense>
    );
  }

  const getRouteSeo = () => {
    const p = location.pathname;
    const parts = p.split('/').filter(Boolean);
    const isLang = parts.length > 0 && validLangs.includes(parts[0]);
    const route = isLang ? parts.slice(1) : parts;

    const byLang = {
      en: {
        player: { title: 'Swiss Table Tennis Player Stats & ELO | TTStats', description: 'Player profile with live ELO rating, match history, head-to-head and performance statistics.' },
        club: { title: 'Swiss Table Tennis Club Stats | TTStats', description: 'Club teams, players, venues and upcoming matches.' },
        league: { title: 'Swiss Table Tennis League Standings | TTStats', description: 'League table, teams, points and match balances.' },
        rankings: { title: 'Swiss Table Tennis Rankings (ELO) | TTStats', description: 'Swiss table tennis rankings based on ELO, with monthly ranking movement and player stats.' },
        tournament: { title: 'Swiss Table Tennis Tournaments | TTStats', description: 'Tournament info and details for Swiss table tennis events.' },
        game: { title: 'Swiss Table Tennis Match Details | TTStats', description: 'Match details, opponents and results.' },
        news: { title: 'Swiss Table Tennis News | TTStats', description: 'Updates and news around TTStats and Swiss table tennis.' },
        calculator: { title: 'Table Tennis ELO Calculator | TTStats', description: 'Estimate ELO changes based on two players\' ratings.' },
        pro: { title: 'TTStats Pro | Advanced Table Tennis Stats', description: 'Unlock advanced features for Swiss table tennis players and clubs.' }
      },
      de: {
        player: { title: 'Tischtennis Spieler Statistiken & ELO | TTStats', description: 'Spielerprofil mit Live ELO, Spielhistorie, Head-to-Head und Leistungsstatistiken.' },
        club: { title: 'Tischtennis Verein Statistiken | TTStats', description: 'Vereinsteams, Spieler, Spielorte und kommende Spiele.' },
        league: { title: 'Tischtennis Liga Tabelle Schweiz | TTStats', description: 'Tabelle, Teams, Punkte und Spielbilanz.' },
        rankings: { title: 'Tischtennis Schweiz Rangliste (ELO) | TTStats', description: 'Schweizer Tischtennis Rangliste auf Basis von ELO mit monatlichen Bewegungen und Spielerstatistiken.' },
        tournament: { title: 'Tischtennis Turniere Schweiz | TTStats', description: 'Turnierinfos und Details zu Schweizer Tischtennis-Events.' },
        game: { title: 'Tischtennis Spiel Details | TTStats', description: 'Spieldetails, Gegner und Resultate.' },
        news: { title: 'Tischtennis News Schweiz | TTStats', description: 'Updates rund um TTStats und Schweizer Tischtennis.' },
        calculator: { title: 'Tischtennis ELO Rechner | TTStats', description: 'ELO-Veränderung basierend auf zwei Ratings schätzen.' },
        pro: { title: 'TTStats Pro | Erweiterte Tischtennis Statistiken', description: 'Erweiterte Funktionen für Schweizer Spieler und Vereine.' }
      },
      fr: {
        player: { title: 'Statistiques Joueur & ELO Suisse | TTStats', description: 'Profil joueur avec ELO en direct, historique, face-a-face et statistiques.' },
        club: { title: 'Statistiques Club Suisse | TTStats', description: 'Equipes, joueurs, salles et matchs a venir.' },
        league: { title: 'Classement Ligue Suisse | TTStats', description: 'Tableau, equipes, points et bilans.' },
        rankings: { title: 'Classement Suisse (ELO) | TTStats', description: 'Classement suisse base sur l\'ELO avec evolution mensuelle et statistiques joueurs.' },
        tournament: { title: 'Tournois Tennis de Table Suisse | TTStats', description: 'Infos et details des tournois en Suisse.' },
        game: { title: 'Details de Match | TTStats', description: 'Details de match, adversaires et resultats.' },
        news: { title: 'Actualites Tennis de Table Suisse | TTStats', description: 'Nouvelles et mises a jour autour de TTStats.' },
        calculator: { title: 'Calculateur ELO | TTStats', description: 'Estimer la variation ELO a partir de deux classements.' },
        pro: { title: 'TTStats Pro | Statistiques Avancees', description: 'Fonctionnalites avancees pour joueurs et clubs suisses.' }
      },
      it: {
        player: { title: 'Statistiche Giocatore & ELO Svizzera | TTStats', description: 'Profilo giocatore con ELO live, cronologia match e statistiche.' },
        club: { title: 'Statistiche Club Svizzera | TTStats', description: 'Squadre, giocatori, sedi e partite in programma.' },
        league: { title: 'Classifica Lega Svizzera | TTStats', description: 'Tabella, squadre, punti e bilanci.' },
        rankings: { title: 'Classifica Svizzera (ELO) | TTStats', description: 'Classifica svizzera basata su ELO con movimento mensile e statistiche giocatori.' },
        tournament: { title: 'Tornei Ping Pong Svizzera | TTStats', description: 'Info e dettagli dei tornei in Svizzera.' },
        game: { title: 'Dettagli Partita | TTStats', description: 'Dettagli partita, avversari e risultati.' },
        news: { title: 'News Ping Pong Svizzera | TTStats', description: 'Aggiornamenti e notizie su TTStats.' },
        calculator: { title: 'Calcolatore ELO | TTStats', description: 'Stimare la variazione ELO da due rating.' },
        pro: { title: 'TTStats Pro | Statistiche Avanzate', description: 'Funzionalita avanzate per giocatori e club svizzeri.' }
      }
    } as const;

    const d = byLang[currentLang];
    const kind = route[0] as keyof typeof d | undefined;
    if (!kind || !d[kind]) return {};
    return d[kind];
  };

  // DISABLED: TTStats Wrapped
  // if (isWrappedRoute) {
  //   return (
  //     <Routes location={location} key={location.pathname}>
  //       <Route path="/:lang/wrapped/2025/:url" element={<Wrapped2025Page />} />
  //       <Route path="/wrapped/2025/:url" element={<RedirectWithParams to="/en/wrapped/2025/:url" />} />
  //     </Routes>
  //   );
  // }

  const isNoindexRoute = (() => {
    const p = location.pathname;
    const parts = p.split('/').filter(Boolean);
    const isLang = parts.length > 0 && validLangs.includes(parts[0]);
    const route = isLang ? parts.slice(1) : parts;
    const root = route[0];

    if (root === '404') return true;
    if (root === 'codes' && route[1] === 'generate') return true;
    if (root === 'club' && route[2] === 'widget') return true;

    return false;
  })();

  return (
    <>
      {/* System Messages Banner */}
      <SystemMessageBanner currentLang={currentLang} />

      {/* Base SEO for all routes; specific pages can override by rendering <SEOHead> themselves */}
      <SEOHead lang={currentLang} {...getRouteSeo()} canonicalPath={location.pathname} noindex={isNoindexRoute} />

      <Layout>
        <Suspense fallback={routeFallback}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              {/* Language-specific routes */}
              <Route path="/:lang" element={<PageTransition><HomePage /></PageTransition>} />
              <Route path="/:lang/player/:url" element={<PageTransition><PlayerPage /></PageTransition>} />
              <Route path="/:lang/calculator" element={<PageTransition><CalculatorPage /></PageTransition>} />
              <Route path="/:lang/club/:id" element={<PageTransition><ClubPage /></PageTransition>} />
              <Route path="/:lang/club/:clubId/widget" element={<PageTransition><WidgetConfiguratorPage /></PageTransition>} />
              <Route path="/:lang/league/:id" element={<PageTransition><LeaguePage /></PageTransition>} />
              <Route path="/:lang/tournament/:id" element={<PageTransition><TournamentPage /></PageTransition>} />
              <Route path="/:lang/game/:id" element={<PageTransition><GamePage /></PageTransition>} />
              <Route path="/:lang/codes" element={<PageTransition><CodesPage /></PageTransition>} />
              <Route path="/:lang/codes/generate" element={<PageTransition><CodeGeneratorPage /></PageTransition>} />
              <Route path="/:lang/pro" element={<PageTransition><ProPage /></PageTransition>} />
              <Route path="/:lang/news" element={<PageTransition><NewsPage /></PageTransition>} />
              <Route path="/:lang/rankings" element={<PageTransition><RankingsLandingPage /></PageTransition>} />
              <Route path="/:lang/404" element={<PageTransition><NotFoundPage /></PageTransition>} />

              {/* Legal pages (no language prefix needed) */}
              <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />
              <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
              <Route path="/404" element={<Navigate to="/en/404" replace />} />

              {/* Default routes (redirect to /en) */}
              <Route path="/" element={<Navigate to="/en" replace />} />
              <Route path="/player/:url" element={<RedirectWithParams to="/en/player/:url" />} />
              <Route path="/calculator" element={<Navigate to="/en/calculator" replace />} />
              <Route path="/club/:id" element={<RedirectWithParams to="/en/club/:id" />} />
              <Route path="/league/:id" element={<RedirectWithParams to="/en/league/:id" />} />
              <Route path="/tournament/:id" element={<RedirectWithParams to="/en/tournament/:id" />} />
              <Route path="/game/:id" element={<RedirectWithParams to="/en/game/:id" />} />
              <Route path="/codes" element={<Navigate to="/en/codes" replace />} />
              <Route path="/codes/generate" element={<Navigate to="/en/codes/generate" replace />} />
              <Route path="/pro" element={<Navigate to="/en/pro" replace />} />
              <Route path="/news" element={<Navigate to="/en/news" replace />} />
              <Route path="/rankings" element={<Navigate to="/en/rankings" replace />} />

              {/* Catch-all 404 route - redirects to English 404 */}
              <Route path="*" element={<Navigate to="/en/404" replace />} />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </Layout>
    </>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <MantineProvider theme={theme}>
        <Notifications position="bottom-center" />
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </MantineProvider>
    </ThemeProvider>
  );
}

export default App;
